import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { posts } from "@/mocks/posts";

const navItems = [
  { label: "首页", to: "/", end: true },
  { label: "汇总清单", to: "/summary", end: false },
  { label: "归档中心", to: "/archive", end: false },
  { label: "关于我们", to: "/about", end: false },
];

const tickerItems = posts.filter((p) => p.hot).map((p) => p.title).slice(0, 6);

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const term = keyword.trim();
    navigate(term ? `/summary?q=${encodeURIComponent(term)}` : "/summary");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-primary-600 text-primary-50">
        <div className="flex h-9 w-full items-center gap-3 px-4 md:px-6">
          <span className="hidden shrink-0 items-center gap-1.5 text-xs font-medium tracking-wide md:flex">
            <i className="ri-flashlight-fill text-accent-300"></i>
            正在热传
          </span>
          <div className="ticker-mask relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-10 whitespace-nowrap text-xs">
              {[...tickerItems, ...tickerItems].map((item, index) => (
                <Link
                  key={`${item}-${index}`}
                  to={`/post/${posts.filter((p) => p.hot)[index % tickerItems.length]?.id ?? ""}`}
                  className="cursor-pointer transition-opacity hover:opacity-70"
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-foreground-950 text-accent-400">
              <i className="ri-goblet-fill text-lg"></i>
            </span>
            <span className="leading-none">
              <span className="block font-heading text-lg font-black tracking-tight text-foreground-950">
                黑料每日
              </span>
              <span className="mt-0.5 block text-[10px] tracking-[0.18em] text-foreground-500">
                DAILY DIGEST
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `cursor-pointer rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden md:block">
              <div className="flex h-9 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-500"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  placeholder="搜索标题或标签"
                  className="w-32 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500 lg:w-40"
                />
              </div>
            </form>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-900 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3">
                <i className="ri-search-line text-base text-foreground-500"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  placeholder="搜索标题或标签"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                />
              </div>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive
                        ? "bg-primary-100 text-primary-700"
                        : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}